"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { useThrottle } from "./useThrottle";

interface UseThrottledScrollOptions {
  throttleMs?: number;
  initialValue?: number;
}

export const useThrottledScroll = (options: UseThrottledScrollOptions = {}) => {
  const { throttleMs = 16, initialValue = 0 } = options;

  const [scrollY, setScrollY] = useState(initialValue);
  const animationFrameRef = useRef<number>(0);

  const updateScroll = useCallback((value: number) => {
    setScrollY(value);
  }, []);

  const throttledUpdate = useThrottle(updateScroll, throttleMs);

  useEffect(() => {
    let isMounted = true;

    const handleScroll = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        if (isMounted) {
          const currentScrollY = window.scrollY;
          throttledUpdate(currentScrollY);
        }
      });
    };

    const initialScrollY = window.scrollY;
    setScrollY(initialScrollY);

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      isMounted = false;
      window.removeEventListener("scroll", handleScroll);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [throttledUpdate]);

  return scrollY;
};
